import React, { useEffect, useRef, useState, useCallback } from "react";
import { updateAddressApi } from "services/orderManagement";
import { toastMessage } from "utils/toastMessage";


let scriptLoadPromise = null;

const loadGoogleMapsScript = (apiKey) => {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    // Already loaded
    if (window.google?.maps?.places?.Autocomplete) {
      resolve();
      return;
    }

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", resolve);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    // ✅ Classic stable API — NO v=beta
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const EditAddressModal = ({ show, onClose, addressDetails,orderId,onSave   }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null); 

  const [mapsReady, setMapsReady] = useState(
    () => !!window.google?.maps?.places?.Autocomplete
  );

  const [formData, setFormData] = useState({
    type: "",
    address: "",
    landmark: "",
    floor: "",
     latitude: "",   // add
  longitude: "",  // add
  pincode: "",    // add
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mapsReady) return;
    loadGoogleMapsScript(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
      .then(() => setMapsReady(true))
      .catch((err) => console.error("Google Maps load error:", err));
  }, []);

  useEffect(() => {
    if (show && addressDetails) {
      setFormData({
        type:     addressDetails.type     || "",
        address:  addressDetails.address  || "",
        landmark: addressDetails.landmark || "",
        floor:    addressDetails.floor    || "",
        latitude:  addressDetails.location?.coordinates?.[1] || "",
      longitude: addressDetails.location?.coordinates?.[0] || "",
      pincode:   addressDetails.pincode   || "",
      });
      setErrors({});
    }
  }, [show, addressDetails]);

  useEffect(() => {
    if (!mapsReady || !show || !inputRef.current) return;
    if (autocompleteRef.current) return;

    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["geocode", "establishment"],
      
    });

    

    ac.addListener("place_changed", () => {
  const place = ac.getPlace();
  
  const addr =
    place.formatted_address ||
    place.name ||
    inputRef.current?.value ||
    "";

  // Extract lat/lng
  const lat = place.geometry?.location?.lat() || "";
  const lng = place.geometry?.location?.lng() || "";

  // Extract pincode from address_components
  const addressComponents = place.address_components || [];
  const pincodeObj = addressComponents.find((c) =>
    c.types.includes("postal_code")
  );
  const pincode = pincodeObj?.long_name || "";

  setFormData((prev) => ({
    ...prev,
    address: addr,
    latitude: lat,
    longitude: lng,
    pincode: pincode,
  }));
  setErrors((prev) => ({ ...prev, address: "" }));
});

    autocompleteRef.current = ac;
  }, [mapsReady, show]);

  // ─── 4. Cleanup when modal closes ────────────────────────────────────────
  useEffect(() => {
    if (!show) {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    }
  }, [show]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (value) setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const handleAddressInput = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, address: val }));
    if (val) setErrors((prev) => ({ ...prev, address: "" }));
  };

  const validate = () => {
    const err = {};
    if (!formData.type)     err.type     = "Please select type";
    if (!formData.address)  err.address  = "Please enter address";
    if (!formData.landmark) err.landmark = "Please enter landmark";
    if (!formData.floor)    err.floor    = "Please enter house No. ";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const reset = () => {
    setFormData({ type: "", address: "", landmark: "", floor: "" });
    setErrors({});
    if (inputRef.current) inputRef.current.value = "";
    if (autocompleteRef.current) {
      window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  };

 

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  setLoading(true);
  let payload = {
    orderId: orderId,
    addressId: addressDetails._id,
    type: formData.type,
    address: formData.address,
    floor: formData.floor,
    landmark: formData.landmark,
    latitude: String(formData.latitude),
    longitude: String(formData.longitude),
    pincode: formData.pincode,
  };

  try {
    await updateAddressApi(payload);
    toastMessage("Address Updated Successfully");

    // ✅ Notify parent to update its state
    if (onSave) {
      onSave({
        type: formData.type,
        address: formData.address,
        floor: formData.floor,
        landmark: formData.landmark,
        latitude: String(formData.latitude),
        longitude: String(formData.longitude),
        pincode: formData.pincode,
      });
    }

    reset();
    onClose();
  } catch (err) {
    console.error("Update address failed:", err);
    toastMessage("Failed to update address. Please try again.", "error");
  } finally {
    setLoading(false);
  }
};

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!show) return null;

  return (
    <>
     
      <div className="edit-addr-overlay">
        <div className="edit-addr-box">
          <h5>Edit Address</h5>

          {/* ── TYPE ── */}
          <div className="form-group">
            <label>Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`form-control ${errors.type ? "is-invalid" : ""}`}
            >
              <option value="">Select Type</option>
              <option value="HOME">Home</option>
              <option value="OFFICE">Office</option>
              <option value="OTHER">Other</option>
            </select>
            {errors.type && (
              <div style={{ color: "red", fontSize: 12, marginTop: 3 }}>
                {errors.type}
              </div>
            )}
          </div>

          {/* ── ADDRESS — classic input with Autocomplete attached ── */}
          <div className="form-group">
            <label>Address</label>
            <input
              ref={inputRef}
              type="text"
              name="address"
              defaultValue={formData.address}
              onChange={handleAddressInput}
              className={`form-control ${errors.address ? "is-invalid" : ""}`}
              placeholder="Start typing your address…"
              autoComplete="off"
            />
            {errors.address && (
              <div style={{ color: "red", fontSize: 12, marginTop: 3 }}>
                {errors.address}
              </div>
            )}
          </div>

          {/* ── LANDMARK ── */}
          <div className="form-group">
            <label>Landmark</label>
            <input
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              className={`form-control ${errors.landmark ? "is-invalid" : ""}`}
              placeholder="e.g. Near Ganga River"
            />
            {errors.landmark && (
              <div style={{ color: "red", fontSize: 12, marginTop: 3 }}>
                {errors.landmark}
              </div>
            )}
          </div>

          {/* ── HOUSE / FLOOR ── */}
          <div className="form-group">
            <label>House No.</label>
            <input
              name="floor"
              value={formData.floor}
              onChange={handleChange}
              className={`form-control ${errors.floor ? "is-invalid" : ""}`}
              placeholder="e.g. Floor 3, Flat 12B"
            />
            {errors.floor && (
              <div style={{ color: "red", fontSize: 12, marginTop: 3 }}>
                {errors.floor}
              </div>
            )}
          </div>

          {/* ── ACTIONS ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Updating…" : "Update"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditAddressModal;